import React from "react";
import { Helmet } from "react-helmet-async";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { keyframes } from "@mui/system";
import Iconify from "../components/iconify/Iconify";
import useAuth from "../hooks/useAuth";
import {
  useCreateEDojangDomainItemMutation,
  useDeleteEDojangDomainItemMutation,
  useGetEDojangGlobalAccessQuery,
  useGetEDojangAdminDomainsQuery,
  useGetEDojangOverviewQuery,
  useUpdateEDojangGlobalAccessMutation,
  useUpdateEDojangDomainItemMutation,
} from "../features/eDojang/eDojangApiSlice";

const glowPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 rgba(255, 196, 90, 0.0), 0 0 20px rgba(64, 151, 117, 0.16);
  }
  50% {
    transform: scale(1.06);
    box-shadow: 0 0 14px rgba(255, 196, 90, 0.58), 0 0 32px rgba(64, 151, 117, 0.4);
  }
`;

const slowRotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const mistShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const ADMIN_DOMAIN_FALLBACKS = [
  {
    key: "examProgram",
    label: "Prüfungsprogramm",
    description: "Prüfungsteile, Anforderungen und Fortschrittsstufen verwalten.",
  },
  {
    key: "teachingVideos",
    label: "Lehrvideos",
    description: "Video-Lektionen strukturieren und für Mitglieder vorbereiten.",
  },
  {
    key: "learningMaterials",
    label: "Lernmaterialien",
    description: "PDFs, Leitfäden und ergänzende Inhalte organisieren.",
  },
  {
    key: "learningPaths",
    label: "Lernpfade",
    description: "Komplette Lernabläufe und empfohlene Reihenfolgen planen.",
  },
];

const ITEM_STATUS_OPTIONS = [
  { value: "draft", label: "Entwurf" },
  { value: "active", label: "Aktiv" },
];

const MemberComingSoonCard = ({
  message = "",
  title = "Noch nicht verfügbar",
  description = "",
}) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: 3,
      overflow: "hidden",
      background:
        "linear-gradient(130deg, rgba(10,18,34,0.95), rgba(24,42,38,0.9), rgba(20,28,52,0.94))",
      backgroundSize: "220% 220%",
      animation: `${mistShift} 10s ease-in-out infinite`,
      borderColor: "rgba(255, 215, 160, 0.36)",
      position: "relative",
    }}
  >
    <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
      <Stack spacing={2.5} alignItems="center" textAlign="center">
        <Box
          sx={{
            width: 94,
            height: 94,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background:
              "radial-gradient(circle at 35% 35%, rgba(255,255,255,0.34), rgba(12,20,32,0.88))",
            animation: `${glowPulse} 3.8s ease-in-out infinite`,
          }}
        >
          <Iconify
            icon="fa-solid:yin-yang"
            width={58}
            sx={{
              color: "#f4f8ff",
              animation: `${slowRotate} 13s linear infinite`,
              filter: "drop-shadow(0 0 10px rgba(255, 208, 128, 0.82))",
            }}
          />
        </Box>

        <Typography
          variant="h3"
          sx={{
            color: "common.white",
            textTransform: "uppercase",
            letterSpacing: 1.2,
            textShadow: "0 0 16px rgba(255, 208, 128, 0.44)",
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body1"
          sx={{ color: "rgba(240,248,255,0.9)", maxWidth: 560 }}
        >
          {description ||
            "Das E-Dojang für Ihr Online-Lernen öffnet sich bald. Hier warten später Lehrvideos, Prüfungsprogramm und weitere Lerninhalte auf Sie."}
        </Typography>

        {message && (
          <Chip
            label={message}
            sx={{
              color: "rgba(255,255,255,0.92)",
              borderColor: "rgba(255,215,160,0.62)",
              backgroundColor: "rgba(15,26,46,0.55)",
            }}
            variant="outlined"
          />
        )}
      </Stack>
    </CardContent>
  </Card>
);

const AdminSkeletonCard = ({ message = "", totalItems = 0 }) => (
  <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
    <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Iconify
            icon="fa-solid:yin-yang"
            width={24}
            sx={{
              color: "primary.default",
              animation: `${slowRotate} 14s linear infinite`,
            }}
          />
          <Typography variant="h4">E-Dojang</Typography>
        </Stack>

        <Typography variant="body1" color="text.secondary">
          Hier bauen wir den Online-Lernbereich auf. In diesem Bereich
          organisieren Sie später Prüfungsprogramm, Lehrvideos und weitere
          Lerninhalte für Ihre Mitglieder.
        </Typography>

        <Typography variant="body2" color="text.secondary">
          In den Domain-Listen können Sie bereits Platzhalter-Einträge anlegen,
          bearbeiten und entfernen.
        </Typography>

        <Chip
          label={`${totalItems} Einträge gesamt`}
          size="small"
          color={totalItems > 0 ? "success" : "default"}
          sx={{ width: "fit-content" }}
        />

        {message && (
          <Alert severity="info" sx={{ mt: 0.5 }}>
            {message}
          </Alert>
        )}
      </Stack>
    </CardContent>
  </Card>
);

const SuperAdminAccessCard = ({
  enabled = false,
  isLoading = false,
  isSaving = false,
  onToggle = () => {},
}) => (
  <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
    <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
      <Stack spacing={1.2}>
        <Typography variant="h5">Globale E-Dojang-Freigabe</Typography>
        <Typography variant="body2" color="text.secondary">
          Aktivieren Sie diese Option, damit E-Dojang für alle Mandanten unabhängig vom Tarif
          sichtbar ist.
        </Typography>
        <FormControlLabel
          sx={{ ml: 0 }}
          control={
            <Switch
              checked={enabled}
              onChange={onToggle}
              disabled={isLoading || isSaving}
              color="success"
            />
          }
          label={
            enabled
              ? "Für alle Mandanten freigeschaltet"
              : "Noch nicht für alle Mandanten freigeschaltet"
          }
        />
      </Stack>
    </CardContent>
  </Card>
);

export default function EDojangPage() {
  const { isUser, isEditor, isAdmin, isSuperAdmin } = useAuth();
  const hasStaffRole = isUser || isEditor || isAdmin || isSuperAdmin;
  const isRestrictedView = !isSuperAdmin;
  const isRestrictedStaffView = hasStaffRole && !isSuperAdmin;
  const [draftByDomain, setDraftByDomain] = React.useState({});
  const [activeActionKey, setActiveActionKey] = React.useState("");
  const [feedback, setFeedback] = React.useState(null);
  const [editDialog, setEditDialog] = React.useState({
    open: false,
    domainKey: "",
    itemId: "",
    title: "",
    summary: "",
    status: "draft",
  });

  const { data, isLoading, isError } = useGetEDojangOverviewQuery(undefined, {
    skip: !isSuperAdmin,
  });
  const {
    data: globalAccessData,
    isLoading: isLoadingGlobalAccess,
    isError: isGlobalAccessError,
    error: globalAccessError,
  } = useGetEDojangGlobalAccessQuery(undefined, { skip: !isSuperAdmin });
  const {
    data: adminDomainsData,
    isLoading: isLoadingAdminDomains,
    isError: isAdminDomainsError,
    error: adminDomainsError,
  } = useGetEDojangAdminDomainsQuery(undefined, { skip: !isSuperAdmin });
  const isGlobalAccessEnabled = Boolean(globalAccessData?.eDojangEnabledForAllTenants);
  const [createDomainItem] = useCreateEDojangDomainItemMutation();
  const [updateGlobalAccess] = useUpdateEDojangGlobalAccessMutation();
  const [updateDomainItem] = useUpdateEDojangDomainItemMutation();
  const [deleteDomainItem] = useDeleteEDojangDomainItemMutation();

  const domains = adminDomainsData?.domains?.length
    ? adminDomainsData.domains
    : ADMIN_DOMAIN_FALLBACKS.map((domain) => ({ ...domain, items: [] }));

  const getDraft = (domainKey) => ({
    title: draftByDomain?.[domainKey]?.title || "",
    summary: draftByDomain?.[domainKey]?.summary || "",
    status: draftByDomain?.[domainKey]?.status || "draft",
  });

  const getErrorMessage = (error, fallback) =>
    error?.data?.message || fallback;

  const handleToggleGlobalAccess = async (_, checked) => {
    setFeedback(null);
    setActiveActionKey("toggle-global-access");
    try {
      const response = await updateGlobalAccess({
        enabled: checked,
      }).unwrap();
      setFeedback({
        severity: "success",
        text: response?.message || "E-Dojang-Freigabe wurde aktualisiert.",
      });
    } catch (error) {
      setFeedback({
        severity: "error",
        text: getErrorMessage(
          error,
          "E-Dojang-Freigabe konnte nicht aktualisiert werden.",
        ),
      });
    } finally {
      setActiveActionKey("");
    }
  };

  const handleDraftChange = (domainKey, field, value) => {
    setDraftByDomain((previous) => ({
      ...previous,
      [domainKey]: {
        title: previous?.[domainKey]?.title || "",
        summary: previous?.[domainKey]?.summary || "",
        status: previous?.[domainKey]?.status || "draft",
        [field]: value,
      },
    }));
  };

  const handleCreateItem = async (domainKey) => {
    const domainDraft = getDraft(domainKey);
    const title = domainDraft.title.trim();
    const summary = domainDraft.summary.trim();

    if (!title) {
      setFeedback({ severity: "error", text: "Bitte geben Sie einen Titel ein." });
      return;
    }

    setFeedback(null);
    setActiveActionKey(`create-${domainKey}`);
    try {
      const response = await createDomainItem({
        domainKey,
        title,
        summary,
        status: domainDraft.status || "draft",
      }).unwrap();

      setFeedback({
        severity: "success",
        text: response?.message || "Eintrag wurde angelegt.",
      });
      setDraftByDomain((previous) => ({
        ...previous,
        [domainKey]: { title: "", summary: "", status: "draft" },
      }));
    } catch (error) {
      setFeedback({
        severity: "error",
        text: getErrorMessage(error, "Eintrag konnte nicht angelegt werden."),
      });
    } finally {
      setActiveActionKey("");
    }
  };

  const openEditDialog = (domainKey, item) => {
    setEditDialog({
      open: true,
      domainKey,
      itemId: item.id,
      title: item.title || "",
      summary: item.summary || "",
      status: item.status || "draft",
    });
  };

  const closeEditDialog = () => {
    setEditDialog({
      open: false,
      domainKey: "",
      itemId: "",
      title: "",
      summary: "",
      status: "draft",
    });
  };

  const handleSaveEdit = async () => {
    const title = String(editDialog.title || "").trim();
    if (!title) {
      setFeedback({ severity: "error", text: "Bitte geben Sie einen Titel ein." });
      return;
    }

    setFeedback(null);
    setActiveActionKey(`edit-${editDialog.itemId}`);
    try {
      const response = await updateDomainItem({
        domainKey: editDialog.domainKey,
        itemId: editDialog.itemId,
        title,
        summary: String(editDialog.summary || "").trim(),
        status: editDialog.status || "draft",
      }).unwrap();
      setFeedback({
        severity: "success",
        text: response?.message || "Eintrag wurde aktualisiert.",
      });
      closeEditDialog();
    } catch (error) {
      setFeedback({
        severity: "error",
        text: getErrorMessage(error, "Eintrag konnte nicht aktualisiert werden."),
      });
    } finally {
      setActiveActionKey("");
    }
  };

  const handleDeleteItem = async (domainKey, item) => {
    if (!window.confirm(`Soll der Eintrag "${item.title}" gelöscht werden?`)) {
      return;
    }

    setFeedback(null);
    setActiveActionKey(`delete-${item.id}`);
    try {
      const response = await deleteDomainItem({
        domainKey,
        itemId: item.id,
      }).unwrap();
      setFeedback({
        severity: "success",
        text: response?.message || "Eintrag wurde gelöscht.",
      });
    } catch (error) {
      setFeedback({
        severity: "error",
        text: getErrorMessage(error, "Eintrag konnte nicht gelöscht werden."),
      });
    } finally {
      setActiveActionKey("");
    }
  };

  const restrictedMessage = isRestrictedStaffView
    ? "Admin-Ansicht: E-Dojang wird in Kürze freigeschaltet."
    : "Mitgliederansicht: E-Dojang wird in Kürze freigeschaltet.";

  const restrictedDescription = isRestrictedStaffView
    ? "Das E-Dojang befindet sich aktuell in Vorbereitung. Sobald die Freigabe erfolgt ist, stehen hier die Lernbereiche für Ihren Verein bereit."
    : "Das E-Dojang für Ihr Online-Lernen öffnet sich bald. Hier warten später Lehrvideos, Prüfungsprogramm und weitere Lerninhalte auf Sie.";

  return (
    <>
      <Helmet>
        <title>Dashboard | E-Dojang</title>
      </Helmet>

      <Container maxWidth="md" sx={{ pb: 4 }}>
        {isRestrictedView ? (
          <MemberComingSoonCard
            title="E-Dojang in Vorbereitung"
            description={restrictedDescription}
            message={restrictedMessage}
          />
        ) : (
          <Stack spacing={2}>
            <SuperAdminAccessCard
              enabled={isGlobalAccessEnabled}
              isLoading={isLoadingGlobalAccess}
              isSaving={activeActionKey === "toggle-global-access"}
              onToggle={handleToggleGlobalAccess}
            />

            {isGlobalAccessError ? (
              <Alert severity="error">
                {getErrorMessage(
                  globalAccessError,
                  "Globale E-Dojang-Freigabe konnte nicht geladen werden.",
                )}
              </Alert>
            ) : null}

            <AdminSkeletonCard
              message={isLoading ? "" : data?.message || ""}
              totalItems={adminDomainsData?.totalItems || 0}
            />

            {feedback?.text ? (
              <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>
                {feedback.text}
              </Alert>
            ) : null}

            {isLoadingAdminDomains ? (
              <Alert severity="info">E-Dojang-Domain-Listen werden geladen.</Alert>
            ) : null}

            {isAdminDomainsError ? (
              <Alert severity="error">
                {getErrorMessage(
                  adminDomainsError,
                  "E-Dojang-Domain-Listen konnten nicht geladen werden.",
                )}
              </Alert>
            ) : null}

            <Grid container spacing={1.5}>
              {domains.map((domain) => {
                const domainDraft = getDraft(domain.key);
                const isCreating = activeActionKey === `create-${domain.key}`;

                return (
                  <Grid key={domain.key} item xs={12} md={6}>
                    <Card variant="outlined" sx={{ borderRadius: 2.4, height: "100%" }}>
                      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                        <Stack spacing={1.5}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            spacing={1}
                          >
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Iconify
                                icon="mdi:circle-slice-8"
                                width={18}
                                sx={{ color: "warning.main" }}
                              />
                              <Typography variant="h6">{domain.label}</Typography>
                            </Stack>
                            <Chip
                              size="small"
                              label={`${domain?.items?.length || 0} Einträge`}
                              color={domain?.items?.length ? "success" : "default"}
                              variant="outlined"
                            />
                          </Stack>

                          <Typography variant="body2" color="text.secondary">
                            {domain.description}
                          </Typography>

                          <Stack spacing={1}>
                            {domain?.items?.length ? (
                              domain.items.map((item) => (
                                <Card
                                  key={item.id}
                                  variant="outlined"
                                  sx={{ borderRadius: 1.8, borderColor: "divider" }}
                                >
                                  <CardContent
                                    sx={{
                                      py: 1.2,
                                      "&:last-child": { pb: 1.2 },
                                    }}
                                  >
                                    <Stack spacing={1}>
                                      <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        spacing={1}
                                      >
                                        <Typography variant="subtitle2">{item.title}</Typography>
                                        <Chip
                                          size="small"
                                          label={item.statusLabel || "Entwurf"}
                                          color={item.status === "active" ? "success" : "default"}
                                          variant={item.status === "active" ? "filled" : "outlined"}
                                        />
                                      </Stack>
                                      {item.summary ? (
                                        <Typography variant="caption" color="text.secondary">
                                          {item.summary}
                                        </Typography>
                                      ) : null}
                                      <Stack direction="row" spacing={1}>
                                        <Button
                                          size="small"
                                          variant="text"
                                          startIcon={<Iconify icon="mdi:pencil-outline" />}
                                          onClick={() => openEditDialog(domain.key, item)}
                                          disabled={activeActionKey.startsWith("delete-")}
                                        >
                                          Bearbeiten
                                        </Button>
                                        <Button
                                          size="small"
                                          color="error"
                                          variant="text"
                                          startIcon={<Iconify icon="mdi:delete-outline" />}
                                          onClick={() => handleDeleteItem(domain.key, item)}
                                          disabled={activeActionKey === `delete-${item.id}`}
                                        >
                                          Löschen
                                        </Button>
                                      </Stack>
                                    </Stack>
                                  </CardContent>
                                </Card>
                              ))
                            ) : (
                              <Alert severity="info" sx={{ borderRadius: 2 }}>
                                Noch keine Einträge vorhanden. Legen Sie den ersten Platzhalter an.
                              </Alert>
                            )}
                          </Stack>

                          <Divider />

                          <Stack spacing={1}>
                            <Typography variant="subtitle2">
                              Neuen Platzhalter-Eintrag anlegen
                            </Typography>
                            <TextField
                              size="small"
                              label="Titel"
                              value={domainDraft.title}
                              onChange={(event) =>
                                handleDraftChange(domain.key, "title", event.target.value)
                              }
                            />
                            <TextField
                              size="small"
                              label="Kurzbeschreibung (optional)"
                              value={domainDraft.summary}
                              multiline
                              minRows={2}
                              onChange={(event) =>
                                handleDraftChange(domain.key, "summary", event.target.value)
                              }
                            />
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1}
                              justifyContent="space-between"
                            >
                              <TextField
                                select
                                size="small"
                                label="Status"
                                value={domainDraft.status}
                                onChange={(event) =>
                                  handleDraftChange(domain.key, "status", event.target.value)
                                }
                                sx={{ minWidth: { xs: "100%", sm: 160 } }}
                              >
                                {ITEM_STATUS_OPTIONS.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <Button
                                variant="contained"
                                startIcon={<Iconify icon="mdi:plus" />}
                                onClick={() => handleCreateItem(domain.key)}
                                disabled={isCreating || !domainDraft.title.trim()}
                              >
                                Eintrag anlegen
                              </Button>
                            </Stack>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Stack>
        )}

        {isError && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            E-Dojang-Status konnte nicht vom Server geladen werden.
          </Alert>
        )}
      </Container>

      {!isRestrictedView ? (
        <Dialog open={editDialog.open} onClose={closeEditDialog} fullWidth maxWidth="sm">
          <DialogTitle>Eintrag bearbeiten</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <TextField
                label="Titel"
                value={editDialog.title}
                onChange={(event) =>
                  setEditDialog((previous) => ({
                    ...previous,
                    title: event.target.value,
                  }))
                }
              />
              <TextField
                label="Kurzbeschreibung"
                value={editDialog.summary}
                multiline
                minRows={3}
                onChange={(event) =>
                  setEditDialog((previous) => ({
                    ...previous,
                    summary: event.target.value,
                  }))
                }
              />
              <TextField
                select
                label="Status"
                value={editDialog.status}
                onChange={(event) =>
                  setEditDialog((previous) => ({
                    ...previous,
                    status: event.target.value,
                  }))
                }
              >
                {ITEM_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeEditDialog}>Abbrechen</Button>
            <Button
              variant="contained"
              onClick={handleSaveEdit}
              disabled={
                activeActionKey === `edit-${editDialog.itemId}` ||
                !String(editDialog.title || "").trim()
              }
            >
              Speichern
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
    </>
  );
}
